import { useEffect, useRef, useState } from "react"
import { useMediaQuery } from "hooks/useMediaQuery"
import { DESKTOP_QUERY } from "components/shared/Layout/breakpoints"

// Compact menu: the side menu can be tucked away for more page space (☰ in
// the top bar, or the [ key — the same key Jira and Confluence use), and
// while it is tucked away it peeks in over the page when the pointer rests on
// the window's left edge. Deliberately not persisted: a reload always brings
// the menu back, so nobody returns the next day to a page with no menu.

/** The theme's sidebar slide (`transition: … 0.4s` in layout-*.css). */
export const MENU_SLIDE_MS = 400

/** The theme's static (push) sidebar layout starts here; below it the menu is a mobile overlay.
 *  The list-and-map pages switch to one pane at a time at the same width. */
export const DESKTOP_MENU_QUERY = DESKTOP_QUERY

export const MENU_SHORTCUT_KEY = "["

/** Width of the left-edge strip that opens a peek. */
export const PEEK_EDGE_PX = 6
/** Page headers, Back buttons and filter bars live up here: no peek. */
export const PEEK_TOP_EXCLUSION_PX = 120
/** Map attributions and bottom action bars live down here: no peek. */
export const PEEK_BOTTOM_EXCLUSION_PX = 48
/** How long the pointer must rest on the edge. Stops a pass-through (a Back
 *  button near the edge, a pointer crossing from a monitor on the left). */
export const PEEK_OPEN_DELAY_MS = 400
/** Slack for a diagonal slip off the open menu before it slides away. */
export const PEEK_CLOSE_DELAY_MS = 300
/** After a peek closes, the edge stays quiet until the pointer has moved this far away. */
export const PEEK_REARM_PX = 48

// dialogs and PrimeReact Sidebars (filter panels, the package item rail) own the keyboard
const MODAL_SELECTOR = ".p-dialog-mask, .p-sidebar-mask, [aria-modal='true']"
const EDITABLE_SELECTOR =
    "input, textarea, select, [contenteditable=''], [contenteditable='true'], [role='textbox'], [role='combobox'], [role='spinbutton']"
export const SIDEBAR_ID = "sidebar-menu"
export const MENU_BUTTON_SELECTOR = ".layout-topbar .menu-button"

/** A mouse or trackpad: hover tooltips and the left-edge peek make sense. Not on touch screens. */
export const HOVER_POINTER_QUERY = "(hover: hover) and (pointer: fine)"

/** The theme's static sidebar is in play (below it ☰ opens the mobile overlay menu instead). */
export const isDesktopMenu = () => window.matchMedia?.(DESKTOP_MENU_QUERY).matches ?? true
export const useIsDesktopMenu = () => useMediaQuery(DESKTOP_MENU_QUERY, true)

/**
 * Intercom's floating launcher sits on the bottom-right corner, where tablets'
 * and phones' own controls live (map toggles, list footers). Below 992px the
 * chat is a "Chat with support" item in the menu drawer instead (and it's
 * never shown in local dev).
 */
export const intercomLauncherHidden = () => import.meta.env.DEV || !isDesktopMenu()
export const useCanHover = () => useMediaQuery(HOVER_POINTER_QUERY, true)

const modalOpen = () => document.querySelector(MODAL_SELECTOR) !== null

/** Where a peek starts: under the top bar, or at the top once it has scrolled away. */
const peekTop = () => Math.max(0, document.querySelector(".layout-topbar")?.getBoundingClientRect().bottom ?? 0)
const setPeekTop = () => document.getElementById(SIDEBAR_ID)?.style.setProperty("--menu-peek-top", `${peekTop()}px`)

/**
 * The [ key toggles the menu, unless it is being typed, is part of a browser
 * or OS shortcut, or something else (a dialog, a page handler) owns the
 * keyboard. AltGr is allowed: Nordic and German layouts type [ with it, and
 * Windows reports AltGr as Ctrl+Alt.
 */
export function isMenuToggleShortcut(event: KeyboardEvent): boolean {
    if (event.key !== MENU_SHORTCUT_KEY) return false
    if (event.defaultPrevented || event.repeat || event.isComposing) return false
    if (event.metaKey || (event.ctrlKey && !event.altKey)) return false
    const target = event.target
    if (target instanceof HTMLElement && (target.isContentEditable || target.closest(EDITABLE_SELECTOR))) return false
    if (modalOpen()) return false
    return isDesktopMenu()
}

/** Registered once when the shell mounts, so a page's own window-level [ handler
 *  would run after it — a page that ever wants [ must stop it in the capture phase. */
export function useMenuToggleShortcut(onToggle: () => void) {
    const onToggleRef = useRef(onToggle)
    onToggleRef.current = onToggle
    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (!isMenuToggleShortcut(event)) return
            event.preventDefault()
            onToggleRef.current()
        }
        window.addEventListener("keydown", onKeyDown)
        return () => window.removeEventListener("keydown", onKeyDown)
    }, [])
}

type MenuPeekOptions = {
    /** The menu is tucked away: the left edge is live. */
    enabled: boolean
    open: boolean
    onOpen: () => void
    onClose: () => void
}

/**
 * Opens the tucked-away menu over the page when the pointer rests on the left
 * edge, and closes it when the pointer leaves it, on a click elsewhere, on
 * Esc, or when focus or the window moves away. The edge ignores drags (map
 * pans, splitters, text selection), touch, and anything under a dialog.
 */
export function useMenuPeek({ enabled, open, onOpen, onClose }: MenuPeekOptions) {
    const onOpenRef = useRef(onOpen)
    const onCloseRef = useRef(onClose)
    onOpenRef.current = onOpen
    onCloseRef.current = onClose
    // false from a peek closing until the pointer has moved away from the edge
    const armedRef = useRef(true)

    useEffect(() => {
        if (!enabled || open) return undefined
        let timer: number | undefined
        const cancel = () => {
            if (timer !== undefined) window.clearTimeout(timer)
            timer = undefined
        }
        const onPointerMove = (event: PointerEvent) => {
            if (event.pointerType === "touch") return
            if (!armedRef.current) {
                if (event.clientX >= PEEK_REARM_PX) armedRef.current = true
                return
            }
            const inZone =
                event.clientX < PEEK_EDGE_PX &&
                event.clientY >= PEEK_TOP_EXCLUSION_PX &&
                event.clientY <= window.innerHeight - PEEK_BOTTOM_EXCLUSION_PX &&
                event.buttons === 0
            if (!inZone) {
                cancel()
                return
            }
            if (timer !== undefined || modalOpen() || !isDesktopMenu()) return
            timer = window.setTimeout(() => {
                timer = undefined
                setPeekTop()
                onOpenRef.current()
            }, PEEK_OPEN_DELAY_MS)
        }
        window.addEventListener("pointermove", onPointerMove, { passive: true })
        window.addEventListener("pointerdown", cancel, true)
        window.addEventListener("blur", cancel)
        // the pointer overshooting the edge out of the window
        document.documentElement.addEventListener("mouseleave", cancel)
        return () => {
            cancel()
            window.removeEventListener("pointermove", onPointerMove)
            window.removeEventListener("pointerdown", cancel, true)
            window.removeEventListener("blur", cancel)
            document.documentElement.removeEventListener("mouseleave", cancel)
        }
    }, [enabled, open])

    useEffect(() => {
        if (!open) return undefined
        const sidebar = document.getElementById(SIDEBAR_ID)
        let closeTimer: number | undefined
        const cancelClose = () => {
            if (closeTimer !== undefined) window.clearTimeout(closeTimer)
            closeTimer = undefined
        }
        const close = () => {
            cancelClose()
            onCloseRef.current()
        }
        const isOnMenu = (target: EventTarget | null) =>
            (target instanceof Node && !!sidebar?.contains(target)) || (target instanceof Element && !!target.closest(MENU_BUTTON_SELECTOR))
        // Where the pointer is, not mouseenter/leave: the menu slides in under a
        // resting pointer, which the browser doesn't count as entering it.
        const onPointerMove = (event: PointerEvent) => {
            if (isOnMenu(event.target)) cancelClose()
            else if (closeTimer === undefined) closeTimer = window.setTimeout(close, PEEK_CLOSE_DELAY_MS)
        }
        const onPointerOutOfWindow = () => {
            if (closeTimer === undefined) closeTimer = window.setTimeout(close, PEEK_CLOSE_DELAY_MS)
        }
        const onPointerDown = (event: PointerEvent) => {
            // (☰ pins the menu open; let its click do that rather than closing first)
            if (!isOnMenu(event.target)) close()
        }
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key !== "Escape") return
            // Esc belongs to the peek while it is open, not to the page
            // underneath (cancel a drawing, back to the list).
            event.preventDefault()
            event.stopImmediatePropagation()
            close()
        }
        const onFocusOut = (event: FocusEvent) => {
            if (event.relatedTarget && !isOnMenu(event.relatedTarget)) close()
        }
        window.addEventListener("pointermove", onPointerMove, { passive: true })
        // the top bar scrolls with the page on ordinary pages
        window.addEventListener("scroll", setPeekTop, { passive: true })
        document.documentElement.addEventListener("mouseleave", onPointerOutOfWindow)
        sidebar?.addEventListener("focusout", onFocusOut)
        window.addEventListener("pointerdown", onPointerDown, true)
        window.addEventListener("keydown", onKeyDown, true)
        window.addEventListener("blur", close)
        return () => {
            cancelClose()
            // the edge stays quiet until the pointer has moved away from it
            armedRef.current = false
            window.removeEventListener("pointermove", onPointerMove)
            window.removeEventListener("scroll", setPeekTop)
            document.documentElement.removeEventListener("mouseleave", onPointerOutOfWindow)
            sidebar?.removeEventListener("focusout", onFocusOut)
            window.removeEventListener("pointerdown", onPointerDown, true)
            window.removeEventListener("keydown", onKeyDown, true)
            window.removeEventListener("blur", close)
        }
    }, [open])
}

/** How long the "where did the menu go?" hint stays up (any click or key dismisses it sooner). */
export const MENU_HINT_MS = 6000
// once per page load: a reload is the only other thing that brings the menu back
let firstHideHintShown = false

/** True while the one-off hint under ☰ should show: after the first hide of a page load. */
export function useFirstHideHint(menuHidden: boolean): boolean {
    const [visible, setVisible] = useState(false)
    const wasHidden = useRef(menuHidden)
    useEffect(() => {
        if (menuHidden && !wasHidden.current && !firstHideHintShown) {
            firstHideHintShown = true
            setVisible(true)
        }
        if (!menuHidden) setVisible(false)
        wasHidden.current = menuHidden
    }, [menuHidden])
    useEffect(() => {
        if (!visible) return undefined
        const dismiss = () => setVisible(false)
        const timer = window.setTimeout(dismiss, MENU_HINT_MS)
        window.addEventListener("pointerdown", dismiss, true)
        window.addEventListener("keydown", dismiss, true)
        return () => {
            window.clearTimeout(timer)
            window.removeEventListener("pointerdown", dismiss, true)
            window.removeEventListener("keydown", dismiss, true)
        }
    }, [visible])
    return visible
}
